import React, { useState, useEffect, useContext, useMemo } from "react";
import { Input, Button, List, Spin, Space } from "antd";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import DriverContext from "../../context/DriverContext";
import "./TransportOrderFilter.css";
import { isColorDark } from "../sideBar/sideBar";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import {
  buildOrderSearchLabel,
  buildOrderSearchValues,
  matchesOrderSearch,
} from "../../utils/orderSearch";

export default function TransportOrderFilter({ onSelectDriver, compact = false }) {
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const { t } = useTranslation();
  const { getFleetID } = useContext(AuthContext);
  const { driversDetailsById } = useContext(DriverContext);
  const fleetId = getFleetID();
  const buttonTextColor = isColorDark(primaryColor) ? "white" : "black";

  useEffect(() => {
    if (!fleetId) return undefined;
    setLoading(true);

    const ordersRef = collection(db, "fleets", fleetId, "transport_orders");
    const unsubscribe = onSnapshot(
      ordersRef,
      (snapshot) => {
        const reactiveOrders = snapshot.docs
          .map((transportOrderDoc) => {
            const data = transportOrderDoc.data();
            return {
              transportOrderId: transportOrderDoc.id,
              transportOrderRef: data.transportOrderRef || "",
              customerReference: data.customerReference || data.transportOrderRef || "",
              internalReference: data.internalReference || transportOrderDoc.id,
              status: data.status || "",
              label: data.label || "",
              name: data.name || "",
              userId: data.userId || "",
              type: data.type || "",
              category: data.category || "",
              categories: Array.isArray(data.categories) ? data.categories : [],
              transfollow: data.transfollow || {},
            };
          })
          .filter((order) =>
            ["ASSIGNED", "PENDING", "ACTIVE", "FINALIZED"].includes(order.status)
          );
        setOrders(reactiveOrders);
        setLoading(false);
      },
      (error) => {
        console.error("Error listening to transport orders", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [fleetId]);

  const filteredSuggestions = useMemo(
    () => orders.filter((order) => matchesOrderSearch(order, query, driversDetailsById)),
    [orders, query, driversDetailsById]
  );

  const handleSelect = (order) => {
    const displayValue =
      buildOrderSearchLabel(order, driversDetailsById) ||
      buildOrderSearchValues(order, driversDetailsById).join(" - ") ||
      order.transportOrderId ||
      "";
    setQuery(displayValue);
    setShowSuggestions(false);
    if (onSelectDriver) {
      onSelectDriver({
        query: displayValue,
        selectedOrderId: order.transportOrderId || "",
      });
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setShowSuggestions(true);
    if (onSelectDriver) {
      onSelectDriver({ query: value, selectedOrderId: "" });
    }
  };

  const handleClear = () => {
    setQuery("");
    setShowSuggestions(false);
    if (onSelectDriver) onSelectDriver({ query: "", selectedOrderId: "" });
  };

  return (
    <div
      className={`transport-filter-wrapper ${
        compact ? "transport-filter-wrapper--compact" : ""
      }`.trim()}
    >
      <Space className="transport-filter-hstack">
        <Input
          size={compact ? "small" : "middle"}
          className="transport-filter-input"
          placeholder={
            t("toFilter.input") ||
            "Filter by order, driver, category or tags"
          }
          value={query}
          onChange={handleChange}
        />
        <Button
          size={compact ? "small" : "middle"}
          style={{
            background: primaryColor,
            color: buttonTextColor,
            borderRadius: 5,
          }}
          onClick={handleClear}
        >
          {t("toFilter.button") || "Limpiar"}
        </Button>
      </Space>

      {showSuggestions && query && filteredSuggestions.length > 0 && (
        <List
          className="transport-filter-list"
          dataSource={filteredSuggestions}
          renderItem={(o, index) => (
            <List.Item
              key={index}
              className="transport-filter-item"
              onClick={() => handleSelect(o)}
            >
              {buildOrderSearchLabel(o, driversDetailsById) || o.transportOrderId}
            </List.Item>
          )}
        />
      )}

      {loading && <Spin />}
    </div>
  );
}
